/** @jsxImportSource @emotion/react */
import { DATADOG_ACTIONS } from '@app/app-constants'
import { blobToDataUrl } from '@app/components/Pages/Map/MapTools/ExportMap/mapExporter'
import { toast } from '@app/components/ui/Toast/toast'
import apiClient from '@app/config/axios.config'
import { useFetchLegends } from '@app/hooks/useFetchLegends'
import { userAtom } from '@app/hooks/useUserProfile'
import { projectedExposureChartStateAtom } from '@app/risk-migration/atoms/projectedExposureChart.atoms'
import { riskMapStyleAtom, selectedFeatureAtom } from '@app/risk-migration/atoms/riskMap.atoms'
import { useRiskLayersState } from '@app/risk-migration/hooks/useRiskLayersState'
import { clientNameAtom } from '@app/state/clientsAtom'
import type { MapLayer } from '@app/types/layerTypes'
import { logDataDogAction } from '@app/utils/logger'
import { pollForReport } from '@app/utils/pollForReport'
import { Print } from '@mui/icons-material'
import { Button, useTheme } from '@mui/material'
import * as turf from '@turf/turf'
import { Feature } from 'geojson'
import { useAtomValue } from 'jotai'
import { Map, MapboxGeoJSONFeature } from 'mapbox-gl'
import { addRasterLayer } from '../../MapView/RiskMapView/LayerTypes'
import {
  addLINZMap,
  getAssetAndHazardDetailsForSelectedFeature,
} from '../../MapView/RiskMapView/RiskMapView.utilities'
import { MapTooltipProps } from '../../MapView/RiskMapView/Tooltip/RiskMapTooltip'
import { useLayerDataFetcher } from '../../RiskSideDrawerContent/data_fetchers/layerDataFetcher'
import { createHiddenDivAndAddToPage, deleteDiv, getMapImageAsBlob } from '../mapExporterUtils'
import { printButton } from './SelectedElementReport.styles'
interface AssetDetails {
  asset: MapTooltipProps['asset'] | undefined | null
  hazards: MapTooltipProps['hazards'] | undefined | null | undefined | null
}

export const SelectedElementReportButton = () => {
  const theme = useTheme()
  const selectedFeature = useAtomValue(selectedFeatureAtom)
  const style = useAtomValue(riskMapStyleAtom)
  const { data: legendsData = {} } = useFetchLegends()
  const { layers } = useRiskLayersState()

  const projectedExposureChartState = useAtomValue(projectedExposureChartStateAtom)
  const layersData = useLayerDataFetcher()
  const clientName = useAtomValue(clientNameAtom)
  const { data: user } = useAtomValue(userAtom)

  const handleClick = async () => {
    if (!selectedFeature) return

    const selectedMapboxGeoJsonFeature = selectedFeature as MapboxGeoJSONFeature

    toast({
      description:
        'Generating data for report. This may take a while, we will notify you when the report is ready.',
      type: 'info',
    })

    const assetLayers = layers.filter((layer) => layer.layerType == 'asset')
    const hazardLayers = layers.filter((layer) => layer.layerType == 'hazard')
    const hazardLayerTypes = hazardLayers.map((hl) => hl.assetTag)
    const selectedAssetLayer = assetLayers.find(
      (assetLayer) => assetLayer.tilesets[0].id === selectedMapboxGeoJsonFeature.layer?.source,
    )

    if (!selectedAssetLayer) throw new Error('Selected asset layer not found')

    const selectedAssetDetails: AssetDetails | null =
      await getAssetAndHazardDetailsForSelectedFeature(
        selectedMapboxGeoJsonFeature,
        assetLayers,
        hazardLayers,
        legendsData,
      )

    if (!selectedAssetDetails || !selectedAssetDetails.asset)
      throw new Error('Asset details not found')

    const assetLayerType = selectedAssetLayer?.type ?? ''
    const selectedAssetName = (selectedAssetDetails.asset?.name ?? 'Element')
      .replace('-', ' ')
      .replace(/\s+/g, '_')
    const assetId = selectedAssetDetails.asset?.id ?? 0
    const date = new Date()
    const year = date.getFullYear()
    const month = date.getMonth() + 1
    const day = date.getDate()

    if (!layersData?.hazardDialogData.hazardDetails) return

    const identifyingInformationCount = Object.keys(
      selectedAssetDetails.asset?.identifyingInformation ?? {},
    ).length

    const showFullPageMap = identifyingInformationCount > 6
    const mapWidthInPixels = 1920
    const mapHeightInPixels = showFullPageMap ? 1449 : 1080

    const hiddenDiv = createHiddenDivAndAddToPage(mapWidthInPixels, mapHeightInPixels)
    const elementMap = await createElementMap(
      hiddenDiv,
      selectedFeature as Feature,
      hazardLayers,
      style,
    )
    const mapImageBlob = await getMapImageAsBlob(elementMap)
    const mapImageDataUrl = await blobToDataUrl(mapImageBlob)
    deleteDiv(hiddenDiv)
    const mapWidthInMetres = turf.distance(
      elementMap.unproject([0, mapHeightInPixels / 2.0]).toArray(),
      elementMap.unproject([mapWidthInPixels, mapHeightInPixels / 2.0]).toArray(),
      { units: 'meters' },
    )

    const mapImageUploadResponse = await apiClient.post(`/report/mapimage`, {
      dataUrl: mapImageDataUrl,
    })

    if (mapImageUploadResponse.status != 200) throw new Error('Map image failed to upload')

    const mapImageDownloadUrl = mapImageUploadResponse.data as string

    const report_id = `${user?.user_uuid}-${Date.now()}`
    try {
      logDataDogAction(DATADOG_ACTIONS.EXPORT_REPORT, {
        type: 'selected-element',
        format: 'pdf',
      })

      await apiClient.post(`/report/export/${clientName}/selected-element-report/${report_id}`, {
        assetId,
        assetLayerType,
        hazardLayerTypes,
        projectedExposureChartState,
        mapImage: {
          bearing: 0,
          pitch: 0,
          mapWidthInMetres,
          url: mapImageDownloadUrl,
          location: elementMap.getCenter(),
        },
      })

      pollForReport(
        report_id,
        `${selectedAssetName}_${assetId}_Risk_Report_${year}-${month}-${day}`,
      )
    } catch (error) {
      toast({
        description:
          'There was an error generating the report. Please notify your Urban Intelligence contact or submit feedback.',
        type: 'error',
      })
    }
  }

  return (
    <>
      <Button css={printButton({ theme })} onClick={handleClick}>
        <Print fontSize="small" />
        &nbsp;&nbsp;Download Element Report
      </Button>
    </>
  )
}

const createElementMap = async (
  hiddenDiv: HTMLDivElement,
  selectedElement: Feature,
  hazardLayers: MapLayer[],
  style: string,
): Promise<Map> =>
  new Promise((resolve) => {
    const bbox = turf.bbox(selectedElement)
    // @TODO: hardcoded linz fix, can't stay like this
    const isLinzBasemap = style.includes('cljgef8p2002t01r7fr970avz')
    const elementMap = new Map({
      container: hiddenDiv,
      style: isLinzBasemap ? { version: 8, sources: {}, layers: [] } : style,
      bounds: [bbox[0], bbox[1], bbox[2], bbox[3]],
      fitBoundsOptions: { maxZoom: 20, padding: 100 },
    })

    elementMap.once('styledata', () => {
      if (isLinzBasemap) addLINZMap(elementMap)
      let lastId = ''
      hazardLayers.forEach((hazardLayer) => {
        hazardLayer.tilesets.forEach((tileset) => {
          addRasterLayer({
            map: elementMap,
            id: tileset.id,
            isVisible: true,
            shouldUpdate: false,
            beforeId: lastId,
          })

          lastId = tileset.id
        })
      })

      elementMap.addSource('elementSource', { type: 'geojson', data: selectedElement })
      elementMap.addLayer({
        id: 'elementLineLayer',
        type: 'line',
        source: 'elementSource',
        paint: {
          'line-color': 'orange',
          'line-width': 10,
        },
      })

      if (selectedElement.geometry.type == 'Point') {
        elementMap.addLayer({
          id: 'elementCircleLayer',
          type: 'circle',
          source: 'elementSource',
          paint: { 'circle-color': 'orange', 'circle-radius': 20 },
        })
      }
    })

    elementMap.once('idle', () => resolve(elementMap))
  })
